/**
 * Featurebase SDK Integration
 * Provides feedback widget with user identification
 *
 * Using SDK identify (Growth plan) instead of SSO (Professional plan)
 * This provides auto-authentication for in-app widgets
 */

const FEATUREBASE_ORG = 'iohpo';
const DEFAULT_BOARD = 'PromptNote';

let featurebaseInitialized = false;
let featurebaseIdentified = false;

/**
 * Load the Featurebase SDK script
 * @returns {Promise<void>}
 */
function loadFeaturebaseScript() {
  return new Promise((resolve, reject) => {
    if (document.getElementById('featurebase-sdk')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'featurebase-sdk';
    script.src = 'https://do.featurebase.app/js/sdk.js';
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.getElementsByTagName('script')[0]?.parentNode?.insertBefore(
      script,
      document.getElementsByTagName('script')[0]
    );
  });
}

/**
 * Initialize Featurebase SDK and feedback widget
 * Safe to call multiple times - will only initialize once
 * @param {object} options - Override default options
 */
export async function initFeaturebase(options = {}) {
  // Only run in browser
  if (typeof window === 'undefined') {
    return;
  }

  // Only initialize once
  if (featurebaseInitialized) {
    return;
  }

  try {
    // Load SDK script
    await loadFeaturebaseScript();

    // Wait for Featurebase to be available
    if (typeof window.Featurebase !== 'function') {
      // Create queue function if not exists
      window.Featurebase = function() {
        (window.Featurebase.q = window.Featurebase.q || []).push(arguments);
      };
    }

    // Initialize feedback widget
    window.Featurebase('initialize_feedback_widget', {
      organization: FEATUREBASE_ORG,
      theme: options.theme || 'light',
      placement: options.placement || 'right', // Floating button on right
      defaultBoard: options.defaultBoard || DEFAULT_BOARD,
      locale: options.locale || 'en',
      metadata: options.metadata || null,
      // Note: email is set via identify, not here
    }, (err, callback) => {
      if (err) {
        console.error('[Featurebase] Widget initialization error', err);
        return;
      }
      if (callback?.action === 'widgetReady') {
        console.log('[Featurebase] Widget ready');
      }
      if (callback?.action === 'feedbackSubmitted') {
        console.log('[Featurebase] Feedback submitted', callback.post);
      }
    });

    featurebaseInitialized = true;
    console.log('[Featurebase] Initialized successfully');
  } catch (error) {
    console.error('[Featurebase] Initialization failed', error);
  }
}

/**
 * Identify user in Featurebase
 * Call this after successful login to link feedback to user
 * @param {string} userId - Unique user ID
 * @param {object} userData - User properties
 * @param {string} userData.email - User email
 * @param {string} userData.name - User display name
 * @param {string} [userData.profilePicture] - User avatar URL
 */
export function identifyFeaturebaseUser(userId, userData = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  if (!userId || !userData.email) {
    console.warn('[Featurebase] Cannot identify user without userId and email');
    return;
  }

  try {
    // Ensure Featurebase function exists
    if (typeof window.Featurebase !== 'function') {
      window.Featurebase = function() {
        (window.Featurebase.q = window.Featurebase.q || []).push(arguments);
      };
    }

    const identifyData = {
      organization: FEATUREBASE_ORG,
      email: userData.email,
      name: userData.name || userData.email.split('@')[0],
      userId: userId,
    };

    // Only add profilePicture if it exists
    if (userData.profilePicture) {
      identifyData.profilePicture = userData.profilePicture;
    }

    window.Featurebase('identify', identifyData, (err) => {
      if (err) {
        console.error('[Featurebase] Identify failed', err);
        return;
      }
      featurebaseIdentified = true;
      console.log('[Featurebase] User identified:', userId);
    });
  } catch (error) {
    console.error('[Featurebase] Identify error', error);
  }
}

/**
 * Clear Featurebase user identity
 * Call this on logout to reset widget to anonymous state
 */
export function clearFeaturebaseIdentity() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Featurebase doesn't have a built-in logout/reset function
    // The identity is stored in localStorage, so we can clear it
    // Pattern: fb_* keys in localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('fb_') || key.startsWith('featurebase'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    featurebaseIdentified = false;
    console.log('[Featurebase] Identity cleared');
  } catch (error) {
    console.error('[Featurebase] Clear identity error', error);
  }
}

/**
 * Open the feedback widget programmatically
 * @param {string} [board] - Optional board to pre-select
 */
export function openFeedbackWidget(board) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const data = { action: 'openFeedbackWidget' };
    if (board) {
      data.setBoard = board;
    }
    window.postMessage({
      target: 'FeaturebaseWidget',
      data: data
    }, '*');
  } catch (error) {
    console.error('[Featurebase] Open widget error', error);
  }
}

/**
 * Check if Featurebase is initialized
 * @returns {boolean}
 */
export function isFeaturebaseInitialized() {
  return featurebaseInitialized;
}

/**
 * Check if user is identified in Featurebase
 * @returns {boolean}
 */
export function isFeaturebaseIdentified() {
  return featurebaseIdentified;
}

/**
 * Update Featurebase widget theme
 * Call this when user changes theme preference
 * @param {'light' | 'dark'} theme - Theme to apply
 */
export function updateFeaturebaseTheme(theme) {
  if (typeof window === 'undefined') {
    return;
  }

  if (!featurebaseInitialized) {
    console.warn('[Featurebase] Cannot update theme before initialization');
    return;
  }

  try {
    if (typeof window.Featurebase !== 'function') {
      return;
    }

    // Re-initialize widget with new theme
    window.Featurebase('initialize_feedback_widget', {
      organization: FEATUREBASE_ORG,
      theme: theme,
      placement: 'right',
      defaultBoard: DEFAULT_BOARD,
      locale: 'en',
    });

    console.log('[Featurebase] Theme updated to:', theme);
  } catch (error) {
    console.error('[Featurebase] Theme update error', error);
  }
}

/**
 * Resolve app theme to Featurebase theme
 * Maps promptnote-web's themes to Featurebase's light/dark
 * @param {string} theme - App theme (light, dark, system)
 * @returns {'light' | 'dark'} Featurebase theme
 */
export function resolveFeaturebaseTheme(theme) {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme === 'dark' ? 'dark' : 'light';
}
